/**
 * Inline Pie Chart for AI Assistant
 *
 * Compact pie chart component for rendering inside chat messages.
 * Uses Recharts (same as dashboard) but without WidgetWrapper.
 */

import React, { useRef, useCallback } from 'react';
import { Box, Typography, Stack, useTheme, IconButton, Tooltip as MuiTooltip, alpha } from '@mui/material';
import { PrintIcon } from "../../icons";
import {useChartPalette} from "../../theme/chartPalette";
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

/**
 * Print a specific element by opening a new window
 */
function printElement(element: HTMLElement, title: string) {
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  if (!printWindow) return;

  const styles = Array.from(document.styleSheets)
    .map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
      } catch {
        return '';
      }
    })
    .join('\n');

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>${title}</title>
        <style>${styles}</style>
        <style>
          body { padding: 20px; font-family: system-ui, -apple-system, sans-serif; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>${element.outerHTML}</body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

export interface InlinePieChartData {
  name: string;
  value: number;
}

export interface InlinePieChartProps {
  title: string;
  data: InlinePieChartData[];
}


export const InlinePieChart: React.FC<InlinePieChartProps> = ({ title, data }) => {
  const theme = useTheme();
  const paleta = useChartPalette();
  const chartRef = useRef<HTMLDivElement>(null);

  const handlePrint = useCallback(() => {
    if (chartRef.current) {
      printElement(chartRef.current, title);
    }
  }, [title]);

  const displayData = data.slice(0, 10);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const chartData = displayData.map((item, index) => ({
    name: item.name,
    value: item.value,
    color: paleta.color(index),
    percentage: total > 0 ? ((item.value / total) * 100).toFixed(1) : '0',
  }));

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload;
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 1,
            borderRadius: 1,
            boxShadow: theme.shadows[2],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" fontWeight="medium">
            {d.name}
          </Typography>
          <Typography variant="caption" display="block" color="text.secondary">
            {d.value.toLocaleString()} ({d.percentage}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (displayData.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No hay datos para mostrar
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={chartRef}
      sx={{
        my: 1,
        p: 1.5,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      <MuiTooltip title="Imprimir" arrow>
        <IconButton
          size="small"
          onClick={handlePrint}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${theme.palette.divider}`,
            padding: '2px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            zIndex: 1,
            '@media print': { display: 'none' },
          }}
        >
          <PrintIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </MuiTooltip>
      <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Chart */}
        <Box sx={{ position: 'relative', width: 150, height: 150, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RechartsPie>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={35}
                outerRadius={55}
                paddingAngle={2}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </RechartsPie>
          </ResponsiveContainer>

          {/* Total in center */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography variant="body1" fontWeight="bold" lineHeight={1}>
              {total.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total
            </Typography>
          </Box>
        </Box>

        {/* Legend */}
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          {chartData.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {item.percentage}%
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};

export default InlinePieChart;
